import { NumberHelper } from "../../util/number-helper";

export namespace PresenterHelper {
    /**
     * `revealedUpTo` is the furthest point this viewer's playhead has reached (a high-water mark),
     * not the current position - see Presenter.tsx. Slides are revealed against it, the active
     * slide is picked with `currentPosition`.
     */
    export const getLiveCurrentSlide = (currentPosition: number | "", tracks: any[], slideSources: any[], revealedUpTo = 0) => {
        const pos = NumberHelper.precision3(currentPosition === "" ? 0 : currentPosition);
        const tracksWithData = tracks.map((el, index) => {
            const source = el.images?.length > 0 ? el.images : slideSources.filter((s: any) => s.collectionId === el?.resource?.id || s.id === el?.resource?.id);
            return { ...el, images: source, index: index };
        });

        // Only the slides this viewer's stream has actually played.
        //
        // The admin publishes a slide the instant they present it and the notification arrives over
        // the websocket in real time, but every viewer plays some seconds behind the live edge.
        // Handing the full list to the UI revealed a slide - in the thumbnail strip and in the
        // counter - before the part of the stream it belongs to had been played. Gating here means
        // the presentation can never run ahead of the video, however early the message arrives.
        //
        // The gate is the high-water mark rather than the current position so that seeking back
        // inside the DVR window does not take slides away again: once the stream has played a
        // slide it stays in the strip and stays reachable.
        const revealLimit = Math.max(NumberHelper.precision3(revealedUpTo), pos);
        const reachedSlides = tracksWithData.filter((el) => revealLimit >= NumberHelper.precision3(el.start));

        // Search newest first, so the last slide covering `pos` wins. This runs on a copy because
        // `reverse()` reorders in place: reversing to search would also reorder what is returned,
        // and it would do so a different number of times depending on which lookup matched.
        // These still test `pos`, not `revealLimit`: after seeking back the active slide is the one
        // covering where the viewer now is, not the newest one that has ever been shown.
        const newestFirst = [...reachedSlides].reverse();
        const slide = newestFirst.find((el) => pos >= NumberHelper.precision3(el.start) && !el.end)
            ?? newestFirst.find((el) => pos >= NumberHelper.precision3(el.start) && pos < NumberHelper.precision3(el.end));

        return {
            currentSlide: slide ?? {},
            // Index within the revealed slides, so it lines up with what the strip renders and with
            // the `current/total` counter built from the same list.
            currentSlideIndex: slide ? reachedSlides.findIndex((el) => el.id === slide.id) : -1,
            // Oldest first, matching the order slides were published: the strip renders left to right.
            presentedSlides: reachedSlides,
        };
    };

    export const getCurrentSlide = (currentPosition: number | "", slideTracks: any[]) => {
        const pos = NumberHelper.precision3(currentPosition === "" ? 0 : currentPosition);
        let currentSlide = {};
        let currentSlideIndex = -1;
        slideTracks.forEach((el: any, index: number) => {
            if (pos >= NumberHelper.precision3(el.start) && pos < NumberHelper.precision3(el.end)) {
                currentSlide = el;
                currentSlideIndex = index;
            }
        });
        return { currentSlide, currentSlideIndex };
    };

    /**
     * Whether the slides layout should be used at all.
     *
     * Several components have to agree on this - the layout itself, the top controls, the control
     * bar's chat button and the chat window's own placement - so it lives here rather than being
     * re-derived in each of them.
     *
     * Deliberately independent of whether a live has started: the layout is shown for the whole
     * pre-live wait too, with the deck's first slide previewed in the slide panel (see Presenter).
     */
    export const hasPresentationLayout = ({ publishedSlides, media }: { publishedSlides?: any[]; media?: any }) => {
        return (publishedSlides?.length ?? 0) > 0
            || (media?.tracks?.filter((track: any) => track.name === 'pdf-track').length ?? 0) > 0;
    };

    /**
     * The height the slides layout needs, composed from its rows because it has none of its own.
     * The video/slide row is the only one that shrinks, and `aspect-ratio` keeps its items at
     * their full size while it does.
     */
    export const measureNaturalHeight = (root?: HTMLElement | null, content?: HTMLElement | null) => {
        if (!root) {
            return 0;
        }
        const inFlow = (element: Element) => !['absolute', 'fixed'].includes(window.getComputedStyle(element).position);
        const heightOf = (element: HTMLElement) => element.getBoundingClientRect().height;

        return Math.ceil((Array.from(root.children).filter(inFlow) as HTMLElement[]).reduce((total, row) => {
            if (row !== content) {
                return total + heightOf(row);
            }
            const style = window.getComputedStyle(row);
            const items = (Array.from(row.children).filter(inFlow) as HTMLElement[]).map(heightOf);
            const stacked = style.gridTemplateColumns.trim().split(/\s+/).length < 2;
            return total + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + (stacked
                ? items.reduce((sum, height) => sum + height, 0) + (parseFloat(style.rowGap) || 0) * (items.length - 1)
                : Math.max(0, ...items));
        }, 0));
    };

    export const getPreviewUrl = (media: any) => {
        const videoStreams = (media?.streams ?? []).find((stream: any) => stream.mimeType === "video/mp4");
        if (!videoStreams) {
            return null;
        }

        return videoStreams.url;
    };

};
